import { readWithQrScanner } from "@pocketsign/in-app-sdk";
import type { useNavigate } from "@tanstack/react-router";
import toast from "react-hot-toast";
import { t } from "./i18n";
import { reportSDKResult, sdk } from "./sdk";

const cushionServiceId = import.meta.env.VITE_CUSHION_SERVICE_ID as string;

type NavigateFunction = ReturnType<typeof useNavigate>;

type ScanOptions = {
  filterServiceIds?: string[];
  navigateFn: NavigateFunction;
};

export const scanQrCode = async ({
  filterServiceIds = [],
  navigateFn,
}: ScanOptions) => {
  const filter = [...filterServiceIds, cushionServiceId];

  const response = await reportSDKResult("readWithQrScanner", () =>
    readWithQrScanner(sdk, {
      filter,
    }),
  );

  if (response.result === "success") {
    const { u: to } = response;

    // QRコード内のuパラメータが絶対URLであるケースは想定していないため、
    // リダイレクタ防止のためhttpから始まる場合は許可しない
    if (to.startsWith("http")) {
      toast.error(t("利用できないURLです"));
      return;
    }

    // 共通QRコード
    // u=/s/{storeSlug} または u=/s/{storeSlug}?t={qrId}
    if (to.startsWith("/s/")) {
      // storeSlug と t(qrId) を混ぜて path に埋め込むと t に issuedAt が連結されるため、明示的に分解する
      const url = new URL(to, window.location.origin);
      const storeSlug = url.pathname.slice("/s/".length);
      // 翻訳関数の t を隠さないよう、URL パラメータ名ではなく実体の名前で受ける
      const qrId = new URLSearchParams(url.search).get("t") ?? undefined;
      // issuedAt: ブラウザが古い支払いURLを復元した際に、経過時間から失効させるために付与する
      navigateFn({
        to: "/pay/$storeSlug",
        params: { storeSlug },
        search: { ...(qrId ? { t: qrId } : {}), issuedAt: Date.now() },
      });
      return;
    }

    // 招待コードQRコードの場合は、招待コード画面に遷移する
    if (to.startsWith("/i/")) {
      navigateFn({ to });
      return;
    }

    // 配布コードQRコードの場合は、配布コード画面に遷移する
    if (to.startsWith("/d/")) {
      navigateFn({ to });
      return;
    }

    // スタンドアロン配布コードQRコードの場合は、/sd/画面に遷移する
    if (to.startsWith("/sd/")) {
      navigateFn({ to });
      return;
    }

    // 北海道世帯給付コードQRコードの場合は、/hkp/画面に遷移する
    if (to.startsWith("/hkp/")) {
      navigateFn({ to });
      return;
    }

    // ポイント譲渡用QRコードの場合は、ポイント譲渡画面に遷移する
    if (to.startsWith("/transfer/accept/")) {
      navigateFn({ to });
      return;
    }

    toast.error(t("利用できないQRコードです"));
  }

  if (response.result === "error") {
    toast.error(t("QRコードの読み取りに失敗しました"));
  }
};
